"use client";

import Loading from "@/components/Loading";
import LoginForm from "@/components/partials/auth/login-form";
import RegForm from "@/components/partials/auth/reg-from";
import { Button } from "@/components/ui/ButtonShadcn";
import Card from "@/components/ui/Card";
import InvestmentCalculator from "@/components/ui/InvestmentCalculator";
import InvestmentInput from "@/components/ui/InvestmentInput";
import Modal from "@/components/ui/Modal";
import useAuthInfo from "@/store/auth";
import { AjaxGet } from "@/util/Ajax";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";

const Page = () => {
  const userData = useAuthInfo((state) => state);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();
  const [total, setTotal] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [investmentAmount, setInvestmentAmount] = useState(500);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const formRef = useRef(null);

  const HydratePage = async () => {
    await useAuthInfo.persist.rehydrate();
    setIsHydrated(true);
    setIsLoading(false);
  };

  useEffect(() => {
    //total must be investmentAmount + 5% of investmentAmount
    setTotal(investmentAmount + investmentAmount * 0.05);
  }, [investmentAmount]);

  useEffect(() => {
    HydratePage();
  }, []);

  const ProceedToCheckout = async (userDataFromRegister, isEft = false) => {
    setIsProcessing(true);
    if (userDataFromRegister) {
      Swal.fire({
        title: "Select Payment Method",
        text: "Please select your payment method",
        icon: "question",
        showDenyButton: true,
        confirmButtonText: "Card",
        denyButtonText: `EFT / Bank Transfer`,
        denyButtonColor: "#231F55",
        confirmButtonColor: "#231F55",
      }).then(async (result) => {
        //Card
        if (result.isConfirmed) {
          var res = await AjaxGet(
            userData.apiToken ? userData : userDataFromRegister,
            `Payment/CreateCartPayment?amount=${investmentAmount}&isEFT=${false}`
          );
          formRef.current.PAY_REQUEST_ID.value = res.data.paY_REQUEST_ID;
          formRef.current.CHECKSUM.value = res.data.checksum;
          setIsProcessing(false);
          formRef.current.submit();
          return
          //EFT
        } else if (result.isDenied) {
          var res = await AjaxGet(
            userData.apiToken ? userData : userDataFromRegister,
            `Payment/CreateCartPayment?amount=${investmentAmount}&isEFT=${true}`
          );
          showEFTPopup(res.data.reference);
          setIsProcessing(false);

          return;
        }
      });
      setIsProcessing(false);
      return;
    }
    var res = await AjaxGet(
      userData.apiToken ? userData : userDataFromRegister,
      `Payment/CreateCartPayment?amount=${investmentAmount}&isEFT=${isEft}`
    );
    if (res.data.checksum == "EFT") {
      showEFTPopup(res.data.reference);
      setIsProcessing(false);

      return;
    }
    formRef.current.PAY_REQUEST_ID.value = res.data.paY_REQUEST_ID;
    formRef.current.CHECKSUM.value = res.data.checksum;
    setIsProcessing(false);
    formRef.current.submit();
  };

  const updateInvestmentValue = (value) => {
    setInvestmentAmount(value);
  };

  const showEFTPopup = (reference) => {
    Swal.fire({
      title: "EFT",
      icon: "info",
      html: `<div class="flex flex-col gap-3 text-left"><p>Please make an EFT payment to the following account details:</p> 
      <div class="flex flex-col gap-1 text-left">
      <p>Amount: <strong>R ${total}</strong></p> 
      <p>Account Name: <strong>EYAM INVESTMENT TECHNOLOGIES (PTY) LTD</strong></p> 
      <p>Bank: <strong>ABSA</strong></p> 
      <p>Account Type: <strong>Current / Cheque</strong></p> 
      <p>Account Number:<strong>4115985575</strong></p> 
      <p>Branch Code:<strong>632005</strong></p> 
      <p>Reference:<strong> ${reference}</strong></p>
      </div>
      <p>After making your payment, please email your proof of payment to <strong>info@eyam.finance</strong> to help us process your transaction faster.</p>
      <button class="bg-primary-500 text-white btn btn inline-flex justify-center print:hidden" onclick="window.print()">Click to print</button>
      </div>`,
    });
  }

  return (
    <div className="  md:mx-8 ">
      <Modal
        activeModal={isLoginModalOpen}
        onClose={setIsLoginModalOpen}
        title="Login"
        labelClass="btn-outline-dark"
        footerContent={
          <div className="flex flex-row gap-4">
            <Button
              text="close"
              onClick={() => {
                setIsLoginModalOpen(false);
              }}
            />
          </div>
        }
      >
        <LoginForm
          onSuccess={() => {
            setIsLoginModalOpen(false);
          }}
        />
      </Modal>
      {isLoading && <Loading />}
      {!isLoading && (
        <div>
          <form
            ref={formRef}
            action="https://secure.paygate.co.za/payweb3/process.trans"
            method="POST"
            target="_self" // Ensures the form submits in the same tab
            style={{ display: "none" }} // Hide the form
          >
            <input type="hidden" name="PAY_REQUEST_ID" />
            <input type="hidden" name="CHECKSUM" />
          </form>
          <h1 className="text-center text-3xl mb-4">Supply Today</h1>
          <div className="flex flex-col md:flex-row gap-4 md:gap-0">
            <div className="flex flex-col md:w-2/3 md:m-6 gap-4">
              <Card className="shadow-md bg-gray-50">
                <label className="text-sm font-bold mb-2 block">
                  Enter Your Supply Amount
                </label>
                <InvestmentInput
                  initalValue={investmentAmount}
                  valueUpdated={updateInvestmentValue}
                />
                <small className="text-gray-500">
                  * Minimum supply amount is R 100
                </small>
              </Card>
              <Card className="shadow-md bg-gray-50">
                <InvestmentCalculator
                  initialInvestmentValue={investmentAmount}
                  initialMonthlyContribution={500}
                />
              </Card>
            </div>
            <div className="flex flex-col md:w-1/3 md:m-6 md:mb-0 mb-6 ">
              <div className="rounded-xl bg-gray-100 shadow-lg p-6 sticky top-32">
                <div className="flex flex-row justify-between mb-4">
                  <h4>Supply</h4>
                  <h4>R {investmentAmount.toFixed(2)}</h4>
                </div>
                <div className="flex flex-row justify-between mb-4">
                  <div className="flex flex-col ">
                    <h4>Admin Fee</h4>
                    <Link
                      className=" align-bottom flex text-left"
                      href={"/termsandconditions#Admin-Fee"}
                      target="_blank"
                    >
                      <small className="flex flex-row text-xs  text-gray-500">
                        5% of supply
                      </small>
                    </Link>
                  </div>

                  <h4>R {(investmentAmount * 0.05).toFixed(2)}</h4>
                </div>
                <hr />
                <div className="flex flex-row justify-between mt-2 ">
                  <h3 className="font-bold">Total</h3>
                  <h3 className="font-bold">R {total.toFixed(2)}</h3>
                </div>
                {!userData.apiToken && (
                  <>
                    <div className="flex flex-col">
                      <div className="flex flex-row mt-4 mb-2">
                        <h5 className="flex flex-col w-3/4">
                          Please Complete the Registration form or login
                        </h5>
                        <div className="ml-auto ">
                          <Button
                            icon="heroicons-outline:login"
                            text="Login"
                            className="bg-primary-500 text-white   mt-2 p-2 "
                            onClick={() => setIsLoginModalOpen(true)}
                          />
                        </div>
                      </div>
                      <RegForm
                        onRegisterSuccess={ProceedToCheckout}
                        showRegisterButton={false}
                        isCheckOut={true}
                      />
                    </div>
                  </>
                )}
                {userData.apiToken && (
                  <>
                    <Button
                      icon="heroicons-outline:credit-card"
                      text="Pay with Card"
                      className="bg-primary-500 text-white w-full mt-4"
                      isLoading={isProcessing}
                      disabled={isProcessing}
                      onClick={() => ProceedToCheckout()}
                    />
                    <Button
                      icon="heroicons-outline:banknotes"
                      text="Pay with EFT"
                      className="bg-primary-500 text-white w-full mt-4"
                      isLoading={isProcessing}
                      disabled={isProcessing}
                      onClick={() => ProceedToCheckout(undefined, true)}
                    />
                  </>
                )}

                <div className="pt-4 w-fit">
                  <small className="text-gray-500">Secured by</small>
                  <Link
                    className="mt-4 w-fit"
                    href={"https://payfast.io/solutions/gateway/"}
                    target="_blank"
                  >
                    <Image
                      src={"/assets/images/paygate/paygate-seeklogo.svg"}
                      width={100}
                      height={24.98}
                    />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Page;
